import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { CreditSubmissionDecisionViewModel, VendorOpportunity } from '@trnsact/trnsact-shared-types';
import { CreditSubmission } from '@trnsact/trnsact-shared-types/src/generated';
import { logError } from 'utils/logger';
import { CALCULATE_FINANCE_PAYMENTS } from 'modules/creditSubmission/lenderDeskingPanel/api/calculateFinancePayments';
import { normalizePaymentsByTerm } from '../lib';
import {
  collectFinanceVariablesForCalc,
  ConvertParams,
  deskingDecisionMapper,
  getFormDefaultValues,
} from 'modules/desking';

interface UseApprovalPricingParams {
  creditSubmissionDecision: CreditSubmissionDecisionViewModel | undefined;
  creditSubmission: CreditSubmission;
  vo: VendorOpportunity;
}

export const useApprovalPricing = ({ creditSubmissionDecision, creditSubmission, vo }: UseApprovalPricingParams) => {
  const [pricing, setPricing] = useState<Record<string, any>>({});

  const [calculatePayments, { loading: isCalculating }] = useLazyQuery(CALCULATE_FINANCE_PAYMENTS);

  useEffect(() => {
    if (!creditSubmissionDecision || !vo) return;

    // Pass just the vo to getFormDefaultValues since we don't need other values for the calculations
    const formDefaultValues = getFormDefaultValues(vo, {}, [], [], '');

    const formData = deskingDecisionMapper.processDecisionToDeskingForm(formDefaultValues, creditSubmissionDecision);

    const { financeQuote, saleDetails, submission } = formData;

    const calculationsVariables: ConvertParams = {
      program: financeQuote,
      submission,
      saleDetails,
      proposalMenu: null,
      isExcludeEquipmentSales: formData.displaySettings.excludeEquipmentSales,
      proposalProductConfigurationsInMenu: {},
      financeProgramDescription: '',
    };

    const variables = collectFinanceVariablesForCalc(calculationsVariables);

    calculatePayments({ variables })
      .then(response => {
        const options = response?.data?.calculateMultiplePayments?.[0]?.paymentOptions ?? [];
        setPricing(normalizePaymentsByTerm(options));
      })
      .catch(logError);
  }, [creditSubmissionDecision, creditSubmission, vo, calculatePayments]);

  return { isCalculating, pricing };
};
